/* eslint-disable react/prop-types */
"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";

import ChatParticipants from "./ChatParticipants";
import ChatFeed from "./ChatFeed";
import ChatOverlay from "./ChatOverlay";
import PinnedMessageBanner from "./PinnedMessageBanner";
import DirectChatBlockModal from "./DirectChatBlockModal";
import DirectChatManageModal from "./DirectChatManageModal";
import StaffEventModerationCard from "./StaffEventModerationCard";
import AutoFitHeaderTitle from "./AutoFitHeaderTitle";
import ChatNotificationBellIcon from "./ChatNotificationBellIcon";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { BADGES } from "../../constants/badges";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
} from "../../utils/deletedAccount";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { formatChatHeaderEventDate } from "../../utils/chatHeaderDate";
import { formatCompactNumber } from "../../utils/compactNumber";
import ValidationPendingNotice from "../events/ValidationPendingNotice";

const CHAT_PANEL_COLOR = "#0D1428";
const CHAT_PRIMARY_BLUE = "#3E6FFB";
const DIRECT_PROFILE_CAPSULE_SHAPE_CLASS =
  "rounded-bl-[67px] rounded-br-[24px] rounded-tl-[67px] rounded-tr-[24px]";
const BALANCE_ICON_CLASS = "h-[18px] w-[18px] shrink-0 object-contain";
const CHAT_BODY_FADE_VARIANTS = {
  initial: { opacity: 0, filter: "blur(5px)" },
  animate: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    filter: "blur(5px)",
    transition: { duration: 0.14, ease: [0.22, 1, 0.36, 1] },
  },
};

function resolveEventHeaderImageSrc(event) {
  const iconImage = String(event?.icon_image_url || "").trim();
  if (iconImage) return iconImage;

  const badgeLabel = String(event?.badge_label || "").trim();
  return BADGES.find((badge) => badge.id === badgeLabel)?.src || null;
}

function resolveTypingAvatarSrc(user) {
  return resolveUserAvatarSrc(user);
}

function resolveDirectAvatarSrc(peer) {
  return resolveUserAvatarSrc(peer);
}

function DesktopChatShell({ children, modalHostRef }) {
  return (
    <div className="relative flex h-full min-h-0 w-full items-center justify-center bg-transparent px-6 py-6">
      <div
        data-chat-overlay-scope
        className="relative isolate flex h-full w-full max-w-[900px] flex-col overflow-hidden"
        style={{
          maxHeight: "min(874px, calc(100vh - 48px))",
        }}
      >
        <div
          data-chat-overlay-body
          className="pointer-events-none absolute inset-x-0 bottom-0 top-[51px] z-0 rounded-[45px]"
          style={{ backgroundColor: CHAT_PANEL_COLOR }}
          aria-hidden="true"
        />
        {modalHostRef ? (
          <div ref={modalHostRef} className="pointer-events-none absolute inset-0 z-[120]" />
        ) : null}
        {children}
      </div>
    </div>
  );
}

function HeaderBackButton({ onClick, ariaLabel, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={`absolute flex h-8 w-8 items-center justify-center text-white transition active:scale-[0.98] ${className}`}
      aria-label={ariaLabel}
    >
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M12.5 4.5L7.5 10L12.5 15.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

function DirectProfileCapsule({ peer, directCode, onOpen, t, notificationsMuted = false, notificationsMutedAriaLabel = "" }) {
  const isDeletedPeer = isDeletedAccountUser(peer);
  const code = isDeletedPeer ? DELETED_USER_LABEL : String(peer?.code || directCode || "---").trim().toUpperCase();
  const username = isDeletedPeer ? "" : formatUserHandle(peer?.username);
  const avatarSrc = resolveDirectAvatarSrc(peer);
  const diamonds = peer?.diamonds ?? peer?.diamond_balance ?? 0;
  const coins = peer?.coins ?? peer?.coin_balance ?? 0;
  const level = peer?.level ?? peer?.storage_level ?? 0;
  const isPremium = !isDeletedPeer && Boolean(peer?.has_premium_subscription || peer?.is_premium || peer?.isPremium);

  return (
    <button
      data-chat-overlay-header
      type="button"
      onClick={() => {
        if (isDeletedPeer) return;
        onOpen?.();
      }}
      className={`absolute left-[42px] right-[42px] top-[11px] flex h-20 items-center justify-between overflow-hidden ${DIRECT_PROFILE_CAPSULE_SHAPE_CLASS} bg-white px-[10px] py-[10px] text-left shadow-[0_14px_28px_rgba(0,0,0,0.16)] transition active:scale-[0.995] ${isDeletedPeer ? "cursor-default" : ""}`}
      aria-label={t?.("chats.direct_chat.open_profile_aria") || username || code}
    >
      <div className="flex min-w-0 flex-1 items-center gap-[10px]">
        <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#D9D9D9]">
          {avatarSrc ? (
            <img
              src={avatarSrc}
              alt={username || code}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : isDeletedPeer ? (
            <span className="text-[13px] font-black leading-none text-black/45">DU</span>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-col">
          {isDeletedPeer ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="max-w-[220px] truncate text-[18px] font-black leading-[22px] text-black">
                {DELETED_USER_LABEL}
              </p>
              {notificationsMuted ? (
                <span
                  className="inline-flex shrink-0 text-black/55"
                  aria-label={notificationsMutedAriaLabel}
                  title={notificationsMutedAriaLabel}
                >
                  <ChatNotificationBellIcon muted size={18} />
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex min-w-0 items-center gap-1.5">
                <PremiumCodeLabel
                  code={code}
                  isPremium={isPremium}
                  outlineColorClassName="border-black"
                  className="max-w-[180px] truncate text-[20px] font-black leading-[22px] text-black"
                  as="p"
                />
                {notificationsMuted ? (
                  <span
                    className="inline-flex shrink-0 text-black/55"
                    aria-label={notificationsMutedAriaLabel}
                    title={notificationsMutedAriaLabel}
                  >
                    <ChatNotificationBellIcon muted size={18} />
                  </span>
                ) : null}
              </div>
              <p className="max-w-[220px] truncate text-[14px] font-bold leading-[18px] text-black">
                {username}
              </p>
            </>
          )}
        </div>
      </div>

      {!isDeletedPeer ? (
        <div className="flex h-[55px] w-[150px] shrink-0 items-center justify-end overflow-hidden">
          <div className="flex shrink-0 items-center gap-[10px]">
            <div className="grid grid-cols-[58px_18px] items-center gap-x-1 gap-y-[6px]">
              <span className="text-right text-[15px] font-semibold leading-[17px] text-black">
                {formatCompactNumber(diamonds)}
              </span>
              <img src="/assets/vault/diamand_icon.svg" alt="" className={BALANCE_ICON_CLASS} />
              <span className="text-right text-[15px] font-semibold leading-[17px] text-black">
                {formatCompactNumber(coins)}
              </span>
              <img src="/assets/vault/coin_icon.svg" alt="" className={BALANCE_ICON_CLASS} />
            </div>
            <div className="flex h-[48px] w-[48px] flex-col items-center justify-center rounded-lg border border-black/20">
              <span className="text-center text-[15px] font-bold leading-[17px] text-black">
                {formatCompactNumber(level)}
              </span>
              <span className="text-center text-[10px] font-semibold leading-[11px] text-black">
                level
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </button>
  );
}

export function EventChatDesktopView({
  headerRef,
  feedRef,
  desktopModalHostRef,

  t,
  language,

  handleBackClick,
  openEventFromChatHeader,
  openClubFromChatHeader,
  headerOpensEvent = false,
  headerTitle,
  isGroupChat = false,
  clubChat = null,
  groupDisplayTitle = "",
  groupParticipantsLabel = "",
  headerSecondaryActionLabel,
  headerSecondaryActionDisabled,
  onHeaderSecondaryAction,
  headerActionIconSrc = "",
  headerActionLabel = "",
  headerActionDisabled = false,
  onHeaderAction = null,
  headerActionActive = false,
  headerAuxActionLabel = "",
  headerAuxActionDisabled = false,
  onHeaderAuxAction = null,
  headerAuxActionActive = false,
  notificationsMuted = false,
  notificationsMutedAriaLabel = "",

  eventName,
  eventDateTime,
  eventType,
  eventForCard = null,
  staffModerationAssessment = null,
  onSendHostRules = null,
  sendingHostRules = false,
  onSendCommercialNotice = null,
  sendingCommercialNotice = false,
  onRejectWithTemplate = null,
  rejectingTemplateCode = "",
  showHostValidationPending = false,
  validationPendingIsConcept = false,
  onOpenHostRules = null,

  eventId,
  participantsEndpoint,
  participantsCount,
  participantsLabelOverride = "",
  participantsDisabled = false,
  setParticipantsCount,
  showParticipants,
  setShowParticipants,
  participantsActionLabel = "",
  participantsActionDisabled = false,
  onParticipantsActionClick = null,
  showHeaderPrimaryCta = false,
  headerPrimaryCtaLabel = "",
  onHeaderPrimaryCtaClick = null,
  showNewJoinersCta = false,
  newJoinersCtaLabel = "",
  onNewJoinersCtaClick = null,

  typingUsers = [],
  onFeedScroll,
  feedBottomPadPx,
  ctxMenu,
  imageViewer,
  items,
  pinnedMessages = [],
  pinnedMessage: legacyPinnedMessage,
  openCtxMenu,
  flashId,
  jumpToMessage,
  avatarByCode,
  openImageViewer,
  openProfileByCode,
  mentionContext,
  currentUserId,
  allowSystemMessageActions = false,
  messageSelectionMode,
  selectedMessageId,
  canSelectMessage,
  onSelectMessage,
  onOpenLocationMessage,
  onVotePoll,
  welcomeCrewPanel = null,

  closeImageViewer,
  copyMessage,
  beginReply,
  beginEdit,
  deleteMessage,
  onTogglePin,
  onToggleReaction,
  canEdit,
  canDelete,
  getHeaderBottomPx,
  closeCtxMenu,

  seenPanel,
  toggleSeenPanel,
  onParticipantsLoaded,
  participantsReloadKey = 0,
  participantSelectionMode = false,
  selectedParticipantId = null,
  canSelectParticipant = null,
  onSelectParticipant = null,

  inputBar,
}) {
  const visiblePinnedMessages = pinnedMessages.length ? pinnedMessages : legacyPinnedMessage?.id ? [legacyPinnedMessage] : [];
  const pinnedMessage = visiblePinnedMessages[visiblePinnedMessages.length - 1] || null;
  const eventHeaderTitle = String(
    headerTitle || eventName || clubChat?.name || groupDisplayTitle || t?.("chats.event_chat.title") || ""
  ).trim();
  const eventHeaderDateLabel = formatChatHeaderEventDate(eventDateTime, eventType, { language, t });
  const participantsLabel = participantsLabelOverride || (isGroupChat
    ? (groupParticipantsLabel || t?.("chats.event_chat.participants", { count: participantsCount }))
    : t?.("chats.event_chat.participants", { count: participantsCount }));
  const showParticipantsAction = Boolean(showParticipants && participantsActionLabel);
  const visibleParticipantsLabel = showParticipantsAction
    ? participantsActionLabel
    : participantsLabel;
  const topTypingUsers = Array.isArray(typingUsers) ? typingUsers.filter(Boolean) : [];
  const topTypingStatusText = topTypingUsers.length > 1 ? "are typing..." : "is typing...";
  const eventHeaderImageSrc = resolveEventHeaderImageSrc(eventForCard);
  const hasFloatingCta = Boolean(showHeaderPrimaryCta || showNewJoinersCta);
  const pinnedBeforeFeed = Boolean(pinnedMessage && !welcomeCrewPanel);
  const headerPrimaryCtaTop = (pinnedBeforeFeed ? 206 : 151) + (topTypingUsers.length ? 43 : 0);
  const newJoinersCtaTop = showHeaderPrimaryCta
    ? headerPrimaryCtaTop + 43
    : headerPrimaryCtaTop;
  const pinnedBannerTop = 8;
  const pinnedFeedPaddingTop = showNewJoinersCta
    ? newJoinersCtaTop + 46 - 138
    : showHeaderPrimaryCta
    ? headerPrimaryCtaTop + 46 - 138
    : topTypingUsers.length ? 108 : 72;
  const topTypingIndicator = topTypingUsers.length ? (
    <div
      className={`${pinnedMessage && welcomeCrewPanel ? "relative mx-auto mt-2 shrink-0" : "absolute left-1/2 -translate-x-1/2"} z-40 flex h-[30px] max-w-[320px] items-center rounded-[40px] bg-white/10 px-3 text-center`}
      style={pinnedMessage && welcomeCrewPanel ? undefined : { top: pinnedMessage ? 68 : 151 }}
    >
      <div className="flex shrink-0 items-center pr-2">
        {topTypingUsers.slice(0, 3).map((user, index) => {
          const avatarSrc = resolveTypingAvatarSrc(user);
          const fallback = String(user?.code || user?.username || "?").slice(0, 1).toUpperCase();
          return (
            <span
              key={user?.id || `${fallback}-${index}`}
              className={`flex h-5 w-5 items-center justify-center overflow-hidden rounded-full border border-[#0D1428] bg-[#5978BF] text-[9px] font-black leading-none text-white ${index ? "-ml-1.5" : ""}`}
            >
              {avatarSrc ? <img src={avatarSrc} alt="" className="h-full w-full object-cover" /> : fallback}
            </span>
          );
        })}
        {topTypingUsers.length > 3 ? (
          <span className="-ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full border border-[#0D1428] bg-[#5978BF] px-1 text-[9px] font-black leading-none text-white">
            +{topTypingUsers.length - 3}
          </span>
        ) : null}
      </div>
      <span className="truncate text-[14px] font-normal leading-[22px] text-white">{topTypingStatusText}</span>
    </div>
  ) : null;

  return (
    <DesktopChatShell modalHostRef={desktopModalHostRef}>
      <div
        ref={headerRef}
        className="relative z-20 h-[138px] shrink-0 overflow-visible"
      >
        <HeaderBackButton
          onClick={handleBackClick}
          ariaLabel={t?.("chats.event_chat.back_aria")}
          className="left-[34px] top-[109px]"
        />

        {onHeaderAuxAction ? (
          <button
            type="button"
            onClick={onHeaderAuxAction}
            disabled={headerAuxActionDisabled}
            className={`absolute right-[76px] top-[109px] flex h-8 w-8 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45 ${
              headerAuxActionActive ? "bg-white/12" : ""
            }`}
            aria-label={headerAuxActionLabel}
          >
            <ChatNotificationBellIcon muted={notificationsMuted} size={24} />
          </button>
        ) : null}

        {onHeaderAction && headerActionIconSrc ? (
          <button
            type="button"
            onClick={onHeaderAction}
            disabled={headerActionDisabled}
            className={`absolute right-[34px] top-[109px] flex h-8 w-8 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45 ${
              headerActionActive ? "bg-white/12" : ""
            }`}
            aria-label={headerActionLabel}
          >
            <img
              src={headerActionIconSrc}
              alt=""
              className={`h-6 w-6 object-contain ${
                String(headerActionIconSrc || "").includes("alert_square") ? "invert" : ""
              }`}
            />
          </button>
        ) : onHeaderSecondaryAction ? (
          <button
            type="button"
            onClick={onHeaderSecondaryAction}
            disabled={headerSecondaryActionDisabled}
            className="absolute right-[34px] top-[107px] flex h-9 min-w-9 items-center justify-center rounded-full bg-white/10 px-3 text-[11px] font-semibold text-white backdrop-blur-sm transition active:scale-[0.98] disabled:opacity-45"
          >
            {headerSecondaryActionLabel}
          </button>
        ) : null}

        <button
          data-chat-overlay-header
          type="button"
          onClick={headerOpensEvent ? openEventFromChatHeader : isGroupChat ? openClubFromChatHeader : openEventFromChatHeader}
          className={`absolute top-[11px] flex h-20 bg-white shadow-[0_10px_22px_rgba(0,0,0,0.08)] transition active:scale-[0.995] ${
            isGroupChat
              ? "left-[42px] right-[42px] flex-col items-center justify-center rounded-[67px] px-8 text-center"
              : "left-[42px] right-[42px] items-center gap-[10px] rounded-[16px] px-[10px] text-left"
          }`}
          aria-label={headerOpensEvent || !isGroupChat ? t?.("chats.event_chat.open_event") : eventHeaderTitle}
        >
          {isGroupChat ? (
            <div className="flex max-w-full items-center justify-center gap-2">
              <AutoFitHeaderTitle
                text={eventHeaderTitle}
                className="text-center font-bold leading-[1.1] text-black"
                wrapperClassName="min-w-0 max-w-full"
              />
              {notificationsMuted ? (
                <span
                  className="inline-flex shrink-0 text-black/55"
                  aria-label={notificationsMutedAriaLabel}
                  title={notificationsMutedAriaLabel}
                >
                  <ChatNotificationBellIcon muted size={19} />
                </span>
              ) : null}
            </div>
          ) : (
            <>
              <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-[14px] bg-[#D9D9D9]">
                {eventHeaderImageSrc ? (
                  <img
                    src={eventHeaderImageSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span className="text-[22px] font-black leading-none text-black/45">
                    {eventHeaderTitle.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <AutoFitHeaderTitle
                    text={eventHeaderTitle.toUpperCase()}
                    className="text-left font-bold leading-[1.1] text-black"
                    wrapperClassName="min-w-0 max-w-full"
                    truncate
                  />
                  {notificationsMuted ? (
                    <span
                      className="inline-flex shrink-0 text-black/55"
                      aria-label={notificationsMutedAriaLabel}
                      title={notificationsMutedAriaLabel}
                    >
                      <ChatNotificationBellIcon muted size={18} />
                    </span>
                  ) : null}
                </div>
                <span className="mt-1 block max-w-full truncate text-[14px] font-medium leading-[18px] text-black">
                  {eventHeaderDateLabel}
                </span>
              </div>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            closeCtxMenu?.();
            if (showParticipantsAction) {
              onParticipantsActionClick?.();
              return;
            }
            setShowParticipants?.(true);
          }}
          disabled={participantsDisabled || (showParticipantsAction && participantsActionDisabled)}
          className={`absolute left-1/2 top-[103px] flex h-[35px] w-[240px] -translate-x-1/2 items-center justify-center rounded-[67px] px-5 text-center transition active:scale-[0.99] disabled:opacity-60 ${
            showParticipantsAction
              ? "border border-white/35 bg-white/[0.08] shadow-[0_8px_20px_rgba(0,0,0,0.16)] backdrop-blur-[10px]"
              : ""
          }`}
          style={showParticipantsAction ? undefined : { backgroundColor: CHAT_PRIMARY_BLUE }}
          aria-label={visibleParticipantsLabel}
        >
          <span className="truncate text-[14px] font-medium leading-[22px] text-white">
            {visibleParticipantsLabel}
          </span>
        </button>

        {!pinnedMessage ? topTypingIndicator : null}

        {showHeaderPrimaryCta ? (
          <div
            className="absolute left-1/2 z-40 -translate-x-1/2 pointer-events-auto"
            style={{ top: `${headerPrimaryCtaTop}px` }}
          >
            <button
              type="button"
              onClick={() => {
                closeCtxMenu?.();
                onHeaderPrimaryCtaClick?.();
              }}
              className="vg-attendance-cta-shimmer rounded-[999px] px-6 py-2 text-[15px] font-semibold leading-4 text-black shadow-[0_8px_18px_rgba(16,185,129,0.34)] transition active:scale-[0.99] whitespace-nowrap"
            >
              {headerPrimaryCtaLabel}
            </button>
          </div>
        ) : null}

        {showNewJoinersCta ? (
          <div
            className="absolute left-1/2 z-40 -translate-x-1/2 pointer-events-auto"
            style={{ top: `${newJoinersCtaTop}px` }}
          >
            <button
              type="button"
              onClick={() => {
                closeCtxMenu?.();
                onNewJoinersCtaClick?.();
              }}
              className="vg-attendance-cta-shimmer rounded-[999px] px-6 py-2 text-[15px] font-semibold leading-4 text-black shadow-[0_8px_18px_rgba(16,185,129,0.34)] transition active:scale-[0.99] whitespace-nowrap"
            >
              {newJoinersCtaLabel}
            </button>
          </div>
        ) : null}
      </div>

      <div className="relative z-0 flex-1 min-h-0">
        <AnimatePresence mode="wait" initial={false}>
          {!showParticipants ? (
            <motion.div
              key="messages"
              className="absolute inset-0 flex min-h-0 flex-col"
              variants={CHAT_BODY_FADE_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <div
                className="pointer-events-none absolute inset-x-0 top-[-12px] z-10 h-[92px]"
                style={{
                  background:
                    "linear-gradient(180deg, #0D1428 0%, rgba(13,20,40,0.96) 26%, rgba(13,20,40,0.7) 58%, rgba(13,20,40,0) 100%)",
                }}
                aria-hidden="true"
              />
              {welcomeCrewPanel ? (
                <div className={`relative z-20 shrink-0 px-6 ${hasFloatingCta ? "pt-24" : "pt-4"}`}>
                  {welcomeCrewPanel}
                </div>
              ) : null}
              <PinnedMessageBanner messages={visiblePinnedMessages} onJump={jumpToMessage} t={t} inline={!!welcomeCrewPanel} topOffset={pinnedBannerTop} />
              {pinnedMessage ? topTypingIndicator : null}
              <div
                ref={feedRef}
                onScroll={onFeedScroll}
                data-chat-message-feed="true"
                style={{
                  paddingBottom: `${feedBottomPadPx}px`,
                  boxSizing: "border-box",
                  ...(pinnedBeforeFeed ? { paddingTop: pinnedFeedPaddingTop } : {}),
                }}
                className={`relative z-0 flex-1 min-h-0 px-6 ${welcomeCrewPanel ? "pt-0" : hasFloatingCta ? "pt-24" : "pt-20"} overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] ${
                  ctxMenu || imageViewer ? "pointer-events-none" : ""
                }`}
              >
                {showHostValidationPending ? (
                  <div className="mb-4">
                    <ValidationPendingNotice
                      compact
                      title={t?.(
                        validationPendingIsConcept
                          ? "chats.event_chat.validation_pending.title_concept"
                          : "chats.event_chat.validation_pending.title_event"
                      )}
                      body={t?.(
                        validationPendingIsConcept
                          ? "chats.event_chat.validation_pending.body_concept"
                          : "chats.event_chat.validation_pending.body_event"
                      )}
                    />
                  </div>
                ) : null}
                <StaffEventModerationCard
                  assessment={staffModerationAssessment}
                  onSendHostRules={onSendHostRules}
                  sendingHostRules={sendingHostRules}
                  onSendCommercialNotice={onSendCommercialNotice}
                  sendingCommercialNotice={sendingCommercialNotice}
                  onRejectWithTemplate={onRejectWithTemplate}
                  rejectingTemplateCode={rejectingTemplateCode}
                />
                <ChatFeed
                  items={items}
                  contextSelectedId={ctxMenu?.item?.id || null}
                  onRequestMenu={({ item, anchorEl, rowEl }) =>
                    openCtxMenu?.({ item, anchorEl, rowEl })
                  }
                  onToggleReaction={onToggleReaction}
                  flashId={flashId}
                  onJumpTo={jumpToMessage}
                  avatarByCode={avatarByCode}
                  onOpenImage={openImageViewer}
                  onOpenProfileByCode={openProfileByCode}
                  variant={isGroupChat ? "group" : "event"}
                  mentionContext={mentionContext}
                  currentUserId={currentUserId}
                  allowSystemMessageActions={allowSystemMessageActions}
                  selectionMode={messageSelectionMode}
                  selectedMessageId={selectedMessageId}
                  canSelectMessage={canSelectMessage}
                  onSelectMessage={onSelectMessage}
                  onOpenLocation={onOpenLocationMessage}
                  onVotePoll={onVotePoll}
                  onOpenHostRules={onOpenHostRules}
                />
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="participants"
              className="absolute inset-0 flex min-h-0 flex-col overflow-hidden"
              variants={CHAT_BODY_FADE_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ChatParticipants
                eventId={eventId}
                participantsEndpoint={participantsEndpoint}
                participantsCount={participantsCount}
                onClose={() => setShowParticipants?.(false)}
                onParticipantsCountChange={(nextCount) => {
                  if (typeof nextCount === "number" && !Number.isNaN(nextCount)) {
                    setParticipantsCount?.(nextCount);
                  }
                }}
                onParticipantsLoaded={onParticipantsLoaded}
                reloadKey={participantsReloadKey}
                selectionMode={participantSelectionMode}
                selectedParticipantId={selectedParticipantId}
                canSelectParticipant={canSelectParticipant}
                onSelectParticipant={onSelectParticipant}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {!showParticipants && inputBar ? (
        <div
          className="absolute inset-x-0 bottom-0 z-30 w-full pb-5"
          style={{ "--bottom-nav-height": "0px" }}
        >
          {inputBar}
        </div>
      ) : null}

      {!showParticipants ? (
        <ChatOverlay
          ctxMenu={ctxMenu}
          onCloseCtxMenu={closeCtxMenu}
          copyMessage={copyMessage}
          beginReply={beginReply}
          beginEdit={beginEdit}
          deleteMessage={deleteMessage}
          onTogglePin={onTogglePin}
          pinnedMessageIds={visiblePinnedMessages.map((pin) => pin.id)}
          onToggleReaction={onToggleReaction}
          canEdit={canEdit}
          canDelete={canDelete}
          getHeaderBottomPx={getHeaderBottomPx}
          imageViewer={imageViewer}
          onCloseImageViewer={closeImageViewer}
          t={t}
          seenPanel={seenPanel}
          onToggleSeen={toggleSeenPanel}
          variant="event"
          restrictBackdropToParent
        />
      ) : null}
    </DesktopChatShell>
  );
}

export function DirectChatDesktopView({
  headerRef,
  feedRef,

  t,

  handleBackClick,

  peer,
  directCode,
  openPeerVault,

  isBlocked,
  handleReportClick,
  handleManageClick,
  canManageChat,
  directLastSeenLabel,
  typingUsers = [],
  notificationsMuted = false,
  notificationsMutedAriaLabel = "",
  canToggleNotifications = false,
  notificationsActionLabel = "",
  notificationsActionDisabled = false,
  onNotificationsAction = null,

  onFeedScroll,
  feedBottomPadPx,
  ctxMenu,
  imageViewer,
  items,
  pinnedMessages = [],
  pinnedMessage: legacyPinnedMessage,
  openCtxMenu,
  flashId,
  jumpToMessage,
  openImageViewer,
  onOpenProfileByCode,
  mentionContext,
  preStartNotice,
  onOpenLocationMessage,
  onVotePoll,
  directInlineReportMessageId,
  onDirectInlineReportClick,
  directInlineReportLabel,
  directInlineReportAria,
  directInlineReportDisabled,
  currentUserId = null,
  allowSystemMessageActions = false,
  onDirectPhotoConsentAction,
  directPhotoConsentSubmitting = false,

  closeCtxMenu,
  copyMessage,
  beginReply,
  beginEdit,
  deleteMessage,
  onTogglePin,
  onToggleReaction,
  canEdit,
  canDelete,
  getHeaderBottomPx,
  closeImageViewer,
  seenPanel,
  toggleSeenPanel,

  blockModalOpen,
  reportModalMode,
  closeBlockModal,
  reportCategory,
  setReportCategory,
  reportComment,
  setReportComment,
  submitBlock,
  blockSubmitting,
  safetyNoticeText,
  manageModalOpen,
  closeManageModal,
  onDeleteChat,
  onDeleteAndBlockChat,
  manageSubmitting,

  inputBar,
}) {
  const visiblePinnedMessages = pinnedMessages.length ? pinnedMessages : legacyPinnedMessage?.id ? [legacyPinnedMessage] : [];
  const pinnedMessage = visiblePinnedMessages[visiblePinnedMessages.length - 1] || null;
  const pinnedBannerTop = 8;
  const directTypingIndicator = Array.isArray(typingUsers) && typingUsers.length ? (
    <div className="absolute left-1/2 z-30 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-[13px] font-medium text-white" style={{ top: pinnedMessage ? 68 : 145 }}>
      {t?.("chats.direct_chat.typing", { defaultValue: "is typing..." })}
    </div>
  ) : null;
  return (
    <DesktopChatShell>
      <div
        ref={headerRef}
        className="relative z-20 h-[194px] shrink-0 overflow-visible"
      >
        <DirectProfileCapsule
          peer={peer}
          directCode={directCode}
          onOpen={openPeerVault}
          t={t}
          notificationsMuted={notificationsMuted}
          notificationsMutedAriaLabel={notificationsMutedAriaLabel}
        />

        <HeaderBackButton
          onClick={handleBackClick}
          ariaLabel={t?.("chats.direct_chat.back_aria", { defaultValue: "Back" })}
          className="left-[34px] top-[111px]"
        />

        <div className="absolute right-[34px] top-[111px] flex items-center gap-3">
          {canToggleNotifications ? (
            <button
              type="button"
              onClick={onNotificationsAction}
              disabled={notificationsActionDisabled}
              className="flex h-8 w-8 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
              aria-label={notificationsActionLabel}
            >
              <ChatNotificationBellIcon muted={notificationsMuted} size={24} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleManageClick}
            disabled={!canManageChat || manageSubmitting}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
            aria-label={t?.("chats.direct_chat.delete_aria")}
          >
            <img
              src="/assets/menu/trash.svg"
              alt=""
              className="h-6 w-6 object-contain"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </button>
          <button
            type="button"
            onClick={handleReportClick}
            disabled={isBlocked}
            className="flex h-8 w-8 items-center justify-center rounded-full text-white transition active:scale-[0.98] disabled:opacity-45"
            aria-label={t?.("chats.direct_chat.report_aria")}
          >
            <img
              src="/assets/menu/alert_square.svg"
              alt=""
              className="h-6 w-6 object-contain invert"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </button>
        </div>

        {directLastSeenLabel ? (
          <div className="absolute left-1/2 top-[103px] z-30 -translate-x-1/2">
            <div
              className="flex h-[35px] w-[240px] max-w-[calc(100vw-360px)] items-center justify-center rounded-[67px] px-4 text-center"
              style={{ backgroundColor: CHAT_PRIMARY_BLUE }}
              aria-label={directLastSeenLabel}
            >
              <span className="truncate text-[14px] font-medium leading-[22px] text-white">
                {directLastSeenLabel}
              </span>
            </div>
          </div>
        ) : null}
        {!pinnedMessage ? directTypingIndicator : null}
      </div>

      <div className="relative z-0 flex-1 min-h-0">
        <div
          className="pointer-events-none absolute inset-x-0 top-[-12px] z-10 h-[92px]"
          style={{
            background:
              "linear-gradient(180deg, #0D1428 0%, rgba(13,20,40,0.96) 26%, rgba(13,20,40,0.7) 58%, rgba(13,20,40,0) 100%)",
          }}
          aria-hidden="true"
        />
        <PinnedMessageBanner messages={visiblePinnedMessages} onJump={jumpToMessage} t={t} topOffset={pinnedBannerTop} />
        {pinnedMessage ? directTypingIndicator : null}
        <div
          ref={feedRef}
          onScroll={onFeedScroll}
          data-chat-message-feed="true"
          style={{
            paddingBottom: `${feedBottomPadPx}px`,
            boxSizing: "border-box",
            ...(pinnedMessage ? { paddingTop: Array.isArray(typingUsers) && typingUsers.length ? 108 : 72 } : {}),
          }}
          className={`relative h-full min-h-0 px-6 pt-20 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] ${
            ctxMenu || imageViewer ? "pointer-events-none" : ""
          }`}
        >
          {safetyNoticeText ? (
            <div className="mb-4 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-center text-[12px] font-semibold leading-[18px] text-white/90">
              {safetyNoticeText}
            </div>
          ) : null}
          <ChatFeed
            items={items}
            contextSelectedId={ctxMenu?.item?.id || null}
            onRequestMenu={({ item, anchorEl, rowEl }) =>
              openCtxMenu?.({ item, anchorEl, rowEl })
            }
            onToggleReaction={onToggleReaction}
            flashId={flashId}
            onJumpTo={jumpToMessage}
            avatarByCode={{}}
            onOpenImage={openImageViewer}
            onOpenProfileByCode={onOpenProfileByCode}
            hideSenderBadge
            variant="direct"
            currentUserId={currentUserId}
            allowSystemMessageActions={allowSystemMessageActions}
            mentionContext={mentionContext}
            onOpenLocation={onOpenLocationMessage}
            onVotePoll={onVotePoll}
            directInlineReportMessageId={directInlineReportMessageId}
            onDirectInlineReportClick={onDirectInlineReportClick}
            directInlineReportLabel={directInlineReportLabel}
            directInlineReportAria={directInlineReportAria}
            directInlineReportDisabled={directInlineReportDisabled}
            onDirectPhotoConsentAction={onDirectPhotoConsentAction}
            directPhotoConsentSubmitting={directPhotoConsentSubmitting}
          />
        </div>
        {preStartNotice ? (
          <div className="absolute inset-0 flex items-center justify-center px-6 pointer-events-none">
            <div className="mb-10 w-full max-w-[420px] rounded-2xl border border-white/35 bg-black/25 px-5 py-4 text-center text-white shadow-[0_10px_35px_rgba(0,0,0,0.3)] backdrop-blur-sm">
              {preStartNotice?.isFree ? (
                <div className="text-[14px] font-semibold">
                  {t?.("chats.direct_chat.start_notice_free")}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-1 text-[14px] font-semibold leading-6">
                    <span>{t?.("chats.direct_chat.start_notice_charge_prefix")}</span>
                    <span className="inline-flex items-center gap-[2px]">
                      <span>{formatCompactNumber(preStartNotice.cost)}</span>
                      <img src="/assets/vault/coin_icon.svg" alt="" className="h-3.5 w-3.5" />
                    </span>
                  </div>
                  <div className="mt-1.5 text-[13px] leading-6 text-white/85">
                    {t?.("chats.direct_chat.start_notice_unlimited")}
                  </div>
                  <div className="mt-1 flex items-center justify-center gap-1 text-[13px] text-white/85">
                    <span>{t?.("chats.direct_chat.start_notice_balance")}</span>
                    <span className="inline-flex items-center gap-[2px]">
                      <span>{formatCompactNumber(preStartNotice.balance)}</span>
                      <img src="/assets/vault/coin_icon.svg" alt="" className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {inputBar ? (
        <div
          className="relative z-30 mt-auto w-full pb-5"
          style={{ "--bottom-nav-height": "0px" }}
        >
          {inputBar}
        </div>
      ) : null}

      <ChatOverlay
        ctxMenu={ctxMenu}
        onCloseCtxMenu={closeCtxMenu}
        copyMessage={copyMessage}
        beginReply={beginReply}
        beginEdit={beginEdit}
        deleteMessage={deleteMessage}
        onTogglePin={onTogglePin}
        pinnedMessageIds={visiblePinnedMessages.map((pin) => pin.id)}
        onToggleReaction={onToggleReaction}
        canEdit={canEdit}
        canDelete={canDelete}
        getHeaderBottomPx={getHeaderBottomPx}
        imageViewer={imageViewer}
        onCloseImageViewer={closeImageViewer}
        t={t}
        seenPanel={seenPanel}
        onToggleSeen={toggleSeenPanel}
        variant="direct"
        restrictBackdropToParent
      />

      <DirectChatBlockModal
        open={!!blockModalOpen}
        mode={reportModalMode}
        onClose={closeBlockModal}
        category={reportCategory}
        onCategoryChange={setReportCategory}
        comment={reportComment}
        onCommentChange={setReportComment}
        onSubmit={submitBlock}
        submitting={!!blockSubmitting}
        t={t}
      />

      <DirectChatManageModal
        open={!!manageModalOpen}
        onClose={closeManageModal}
        onDelete={onDeleteChat}
        onDeleteAndBlock={onDeleteAndBlockChat}
        submitting={!!manageSubmitting}
        t={t}
      />
    </DesktopChatShell>
  );
}

export default EventChatDesktopView;
